/**
 * Пометки сравнения версий поверх превью — режим «дифф» панели превью.
 *
 * ЗАЧЕМ ОТДЕЛЬНЫЙ ФАЙЛ. Сравнение версий обязано рисоваться ТЕМ ЖЕ компонентом, что и превью
 * в режиме чтения (решение заказчика 26.08.2026): второй рендер поста означал бы «сравнили
 * одно, опубликовали другое». Поэтому пометки — не отдельный вид поста, а тонкая накладка на
 * дерево сущностей: `MessagePreview` получает диапазоны, `PreviewNodes` режет по ним текстовые
 * листья. Здесь живут тип диапазонов и сама резка, чтобы `entityNode.tsx` не разросся.
 *
 * ЕДИНИЦЫ. Диапазоны — в UTF-16, как offset/length сущностей в Bot API (§Хребет 1.2).
 * Ответ маршрута сравнения режет текст по графемам, но склейка кусков даёт ровно исходную
 * строку, поэтому счёт по `String.length` при обходе кусков и даёт UTF-16-смещения.
 *
 * ЦВЕТ. Подложки заданы литералами, а не токенами: пометка лежит ВНУТРИ `.tg`, где токенов
 * GeoBoost нет ни одного (docs/ui-design.md §6). Числа взяты те же, что у `--red-bg`,
 * `--red-ink`, `--green-bg`, `--green-ink`, — «убрали» и «добавили» обязаны читаться в
 * интерфейсе одинаково везде.
 */

import { Fragment, type ReactNode } from 'react';
import styles from './MessagePreview.module.css';

/** Полуинтервал `[start, end)` в единицах UTF-16. */
export type DiffRange = readonly [start: number, end: number];

export interface DiffMarks {
  /** `del` — левое полотно (что убрали), `add` — правое (что добавили). */
  readonly kind: 'del' | 'add';
  /** Возрастающие и непересекающиеся — так их строит `diffSides` в `VersionDiff`. */
  readonly ranges: readonly DiffRange[];
}

/**
 * Режет текстовый лист дерева по диапазонам пометок. `start` — абсолютное смещение листа
 * в тексте поста; лист может попасть под пометку целиком, частично или не попасть вовсе.
 *
 * Возвращает саму строку, когда пересечений нет: лишний элемент вокруг каждого листа
 * менял бы разметку превью в обычном режиме без всякой пользы.
 */
export function markText(text: string, start: number, marks: DiffMarks): ReactNode {
  const out: ReactNode[] = [];
  let pos = 0;
  for (const [from, to] of marks.ranges) {
    const a = Math.max(from - start, 0);
    const b = Math.min(to - start, text.length);
    if (b <= a || b <= pos) continue;
    if (a > pos) out.push(<Fragment key={`t${String(pos)}`}>{text.slice(pos, a)}</Fragment>);
    const piece = text.slice(a, b);
    out.push(
      marks.kind === 'del' ? (
        <del key={`m${String(a)}`} className={styles['markDel']}>
          {piece}
        </del>
      ) : (
        <ins key={`m${String(a)}`} className={styles['markIns']}>
          {piece}
        </ins>
      ),
    );
    pos = b;
  }
  if (out.length === 0) return text;
  if (pos < text.length) out.push(<Fragment key={`t${String(pos)}`}>{text.slice(pos)}</Fragment>);
  return <>{out}</>;
}
